import clsx from 'clsx';

import { useToastStore, type ToastTone } from '../stores/toastStore';

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'border-slate-200 bg-white text-slate-800',
  success: 'border-success/40 bg-emerald-50 text-emerald-900',
  warning: 'border-warning/40 bg-amber-50 text-amber-900',
  error: 'border-danger/40 bg-rose-50 text-rose-900',
};

const TONE_DOT: Record<ToastTone, string> = {
  info: 'bg-slate-400',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
};

/**
 * Global toast viewport — listens to the zustand store and renders the active
 * queue in the bottom-right corner. Components push toasts via `useToastStore`.
 */
export function ToastHost(): JSX.Element {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-[calc(100vw-2rem)] flex-col gap-2 sm:max-w-sm"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={clsx(
            'pointer-events-auto flex gap-3 rounded-md border p-3 shadow-md',
            TONE_CLASS[toast.tone],
          )}
        >
          <span className={clsx('mt-1.5 h-2 w-2 flex-none rounded-full', TONE_DOT[toast.tone])} />
          <div className="flex-1">
            <div className="text-sm font-semibold">{toast.title}</div>
            {toast.description !== undefined ? (
              <div className="mt-0.5 text-sm opacity-90">{toast.description}</div>
            ) : null}
          </div>
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-700"
            onClick={() => dismiss(toast.id)}
            aria-label="Cerrar notificación"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
